/**
 * `EmojiPicker` — a lightweight emoji popover for the widget composer (no external library,
 * CSP-safe). A curated set; picking one calls `onPick(emoji)` and the composer inserts it at the
 * caret. Mirrors the dashboard's picker for parity.
 */

import { useState } from 'react';

const EMOJI: string[] = [
  '😀', '😃', '😄', '😁', '😊', '🙂', '😉', '😍',
  '😎', '🤗', '🤔', '👍', '👎', '👌', '🙌', '👏',
  '🙏', '💪', '🎉', '✨', '🔥', '❤️', '💯', '✅',
  '❌', '⚠️', '⭐', '💡', '📌', '📎', '📞', '✉️',
  '⏰', '🚀', '😅', '😂', '🥳', '😇', '🤝', '👋',
];

export function EmojiPicker({ onPick, disabled }: { onPick: (emoji: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Insert emoji"
        title="Emoji"
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-lc-text/60 transition-colors hover:bg-black/5 hover:text-lc-text disabled:opacity-50"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5"
          aria-hidden
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M8.5 14.5a4 4 0 0 0 7 0" />
          <line x1="9" y1="9.5" x2="9" y2="9.5" />
          <line x1="15" y1="9.5" x2="15" y2="9.5" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="absolute bottom-full left-0 z-50 mb-2 w-60 max-w-[calc(100vw-1.5rem)] rounded-lc border border-black/10 bg-white p-2 shadow-xl"
          >
            <div className="grid grid-cols-8 gap-1">
              {EMOJI.map((e) => (
                <button
                  key={e}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onPick(e);
                    setOpen(false);
                  }}
                  className="grid h-7 w-7 place-items-center rounded text-lg hover:bg-black/5"
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default EmojiPicker;
